/**
 * ToolCallTimeline — a vertical timeline of canonical AI tool activity for a
 * call (name, gateway decision, timing). Items come from `useCallToolCalls`
 * (GET .../calls/{callId}/tool-calls), mapped by the page into display-ready
 * strings; a calm EmptyState shows until items exist.
 *
 * The timeline shows AI Tool Gateway decisions only — read-only knowledge
 * lookups are not audited and never appear, and a staff member's manual action
 * from the dashboard is not an AI decision, so it won't show here either.
 *
 * Provider-independent: the items are canonical Hostesa tool-call records,
 * never raw provider tool payloads.
 */

import { useT } from '@hostesa/i18n';
import { EmptyState } from '@hostesa/ui';

/** Canonical, provider-independent tool-call timeline item. */
export interface ToolCallItem {
  id: string;
  /** Display tool name (e.g. `Requested staff handoff`). */
  name: string;
  /** Gateway outcome the backend recorded (e.g. `Executed`, `Denied`). */
  decision?: string;
  /** Display timestamp, already formatted by the caller in the hotel timezone. */
  occurredAtLabel?: string;
}

export interface ToolCallTimelineProps {
  items?: readonly ToolCallItem[];
  'data-testid'?: string;
}

export function ToolCallTimeline({ items = [], 'data-testid': testId }: ToolCallTimelineProps) {
  const { t } = useT('calls');

  if (items.length === 0) {
    return (
      <EmptyState
        data-testid={`${testId ?? 'tool-call-timeline'}-empty`}
        title={t('detail.tools.empty.title')}
        description={t('detail.tools.empty.description')}
      />
    );
  }

  return (
    <ol data-testid={testId ?? 'tool-call-timeline'} className="flex flex-col gap-3">
      {items.map((item) => (
        <li
          key={item.id}
          data-testid={`tool-call-${item.id}`}
          className="flex items-baseline justify-between gap-3 border-l-2 border-[var(--color-warm-stone-soft)] pl-3"
        >
          <span className="text-sm text-[var(--color-charcoal)]">{item.name}</span>
          {item.decision && (
            <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-stone-mid)]">
              {item.decision}
            </span>
          )}
          {item.occurredAtLabel && (
            <time className="text-xs text-[var(--color-stone-mid)]">{item.occurredAtLabel}</time>
          )}
        </li>
      ))}
    </ol>
  );
}
